/**
 * O formulário de uma mensagem pronta nova (#1192).
 *
 * Desceu de Configurações → Mensagens prontas quando a caixa de mensagens
 * prontas da conversa passou a criar também: quem tem a permissão cadastra a
 * saudação no meio do atendimento, sem sair da conversa. Os campos, a ida ao
 * servidor e o que acontece com a lista são os mesmos nas duas telas; cada uma
 * escolhe só o cartão em volta.
 *
 * ── A mensagem entra na lista na hora, e a lista é relida ───────────────
 *
 * A caixa da conversa e a seção das Configurações leem a mesma consulta. Só
 * invalidar deixava, entre a criação e a resposta da releitura, a lista sem a
 * mensagem nova (e, na primeira, o "ainda não tem mensagens prontas" de
 * volta). Ela entra no FIM, e a releitura a põe no lugar que o servidor
 * decide: ordenar aqui com outra regra de caixa e acento faria a linha pular.
 *
 * ── O rascunho pode vir de fora ──────────────────────────────────────────
 *
 * Na caixa da conversa, trocar de aba ou fechar a caixa desmontaria o
 * formulário e jogaria fora o que estava escrito. Lá o rascunho mora no
 * editor da conversa, e vem por `rascunho`/`aoMudarRascunho`; sem eles, o
 * formulário guarda o próprio.
 *
 * ── Um envio por vez, mesmo com o formulário remontado ──────────────────
 *
 * Remontado (Esc e reabrir, trocar de aba e voltar), o formulário nasceria com
 * o mesmo rascunho e o botão livre enquanto o primeiro envio ainda está no
 * caminho; o segundo clique daria 409. `ENVIO` marca o envio na fila de
 * mutações do React Query, que sobrevive ao formulário.
 *
 * ── Telefone e e-mail pedem confirmação ──────────────────────────────────
 *
 * Podem ser da loja, e por isso não são recusados; mas a lista é lida pela
 * organização inteira, e o salvar espera a pessoa dizer que são (decisão de
 * 03/11/2026). CPF e placa, a API recusa.
 */

import { useState, type FormEvent, type ReactNode } from 'react';
import { useIsMutating, useMutation, useQueryClient } from '@tanstack/react-query';
import type { MensagemPronta, MensagensProntas } from '@cliqcrm/contracts';
import { useApi } from '@/shared/api/consultas';
import { chaves } from '@/shared/api/chaves';
import { recadoDeErro } from '@/shared/api/recado-de-erro';
import { Botao, Cartao, type VarianteCartao } from '@/shared/ui';
import { Faixa } from '@/shared/ui/estados/faixa';
import {
  AvisoDeContato,
  CamposDaMensagemPronta,
  RASCUNHO_VAZIO,
  rascunhoCompleto,
  temContatoNovo,
  type RascunhoDeMensagemPronta,
} from '@/entities/atendimento/mensagem-pronta';
import './nova-mensagem-pronta.css';

/**
 * A marca do envio na fila de MUTAÇÕES (ver o cabeçalho). Não é chave de
 * cache: nenhuma consulta a usa, e o SSE não a invalida.
 */
const ENVIO = ['criar-mensagem-pronta'] as const;

/** A lista com a mensagem nova no fim, até a releitura trazer a ordem do servidor. */
function comANova(
  atual: MensagensProntas | undefined,
  criada: MensagemPronta,
): MensagensProntas | undefined {
  if (!atual) return atual;
  return { ...atual, itens: [...atual.itens.filter((m) => m.id !== criada.id), criada] };
}

export function NovaMensagemPronta({
  variante = 'folgado',
  parte,
  focar = false,
  rascunho: rascunhoDeFora,
  aoMudarRascunho,
  aoCriar,
  aoCancelar,
}: {
  variante?: VarianteCartao;
  /** A classe da tela que ajusta o cartão no contexto (`admin__form`). */
  parte?: string;
  /** Põe o cursor no título ao aparecer: na conversa, o formulário abre por um clique. */
  focar?: boolean;
  /** O rascunho guardado por quem monta o formulário. Ver o cabeçalho. */
  rascunho?: RascunhoDeMensagemPronta;
  aoMudarRascunho?: (rascunho: RascunhoDeMensagemPronta) => void;
  aoCriar?: (criada: MensagemPronta) => void;
  /** Sem ele, não há "Cancelar": nas Configurações o formulário fica sempre à vista. */
  aoCancelar?: () => void;
}): ReactNode {
  const api = useApi();
  const cache = useQueryClient();
  const [rascunhoProprio, definirRascunhoProprio] =
    useState<RascunhoDeMensagemPronta>(RASCUNHO_VAZIO);
  const rascunho = rascunhoDeFora ?? rascunhoProprio;
  const guardarRascunho = aoMudarRascunho ?? definirRascunhoProprio;
  const [contatoConfirmado, definirContatoConfirmado] = useState(false);

  // Mudou o texto, a confirmação do telefone ou e-mail de antes não vale mais.
  const mudarRascunho = (novo: RascunhoDeMensagemPronta): void => {
    definirContatoConfirmado(false);
    guardarRascunho(novo);
  };

  const criar = useMutation({
    mutationKey: ENVIO,
    mutationFn: async () => {
      if (!api) throw new Error('Sem sessão.');
      return api.criarMensagemPronta({
        corpo: { titulo: rascunho.titulo.trim(), texto: rascunho.texto.trim() },
      });
    },
    onSuccess: (criada) => {
      mudarRascunho(RASCUNHO_VAZIO);
      cache.setQueryData<MensagensProntas>(chaves.mensagensProntas(), (atual) =>
        comANova(atual, criada),
      );
      void cache.invalidateQueries({ queryKey: chaves.mensagensProntas() });
      aoCriar?.(criada);
    },
  });

  /** Este formulário, ou um anterior com o mesmo rascunho, ainda esperando o servidor. */
  const enviando = useIsMutating({ mutationKey: ENVIO }) > 0;
  const pedeConfirmacao = temContatoNovo(rascunho) && !contatoConfirmado;
  /** Os dois campos preenchidos, nada a caminho e, se houver contato, confirmado. */
  const prontoParaEnviar = rascunhoCompleto(rascunho) && !enviando && !pedeConfirmacao;

  return (
    <Cartao
      como="form"
      variante={variante}
      {...(parte ? { parte } : {})}
      aria-label="Nova mensagem pronta"
      onSubmit={(evento: FormEvent) => {
        evento.preventDefault();
        if (prontoParaEnviar) criar.mutate();
      }}
    >
      <CamposDaMensagemPronta rascunho={rascunho} aoMudar={mudarRascunho} focar={focar} />

      {pedeConfirmacao && <AvisoDeContato aoConfirmar={() => definirContatoConfirmado(true)} />}

      {criar.isError && (
        <Faixa tom="bloqueado">
          {recadoDeErro(criar.error, { generica: 'Não foi possível criar a mensagem pronta.' })}
        </Faixa>
      )}

      <div className="nova-pronta__acoes">
        <Botao
          type="submit"
          variante="primario"
          desabilitado={!prontoParaEnviar}
          motivo={
            enviando
              ? 'Criando…'
              : pedeConfirmacao
                ? 'Confirme o telefone ou e-mail acima.'
                : 'Preencha título e texto.'
          }
        >
          {enviando ? 'Criando…' : 'Criar mensagem'}
        </Botao>
        {aoCancelar && <Botao onClick={aoCancelar}>Cancelar</Botao>}
      </div>
    </Cartao>
  );
}
